import { NetworkStatus, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Note } from 'features/deals/dealCards/Diligence/DealDocumentTypes';
import { AddRemittanceNoteInput, GetRemittanceNotesInput } from '__generated__/globalTypes';
import { ADD_REMITTANCE_NOTE } from 'mutation/remittance';
import { GET_REMITTANCE_NOTES } from 'query/remittance';
import { GET_USER_COMPANY } from 'query/company';
import { GetUserCompany } from 'query/__generated__/GetUserCompany';

const DEFAULT_LIMIT = 25;

type RemittanceNoteItem = {
  offeringID?: string | null;
  buyerCompanyID: string;
  sellerCompanyID: string;
  noteID: string;
  dealID: string;
  loanID?: string | null;
  fileName: string;
  note: string;
  authorID: string;
  authorRole: string;
  createdAt: number | string;
  updatedAt: number | string;
};

type GetRemittanceNotesResponse = {
  getRemittanceNotes: {
    notes: RemittanceNoteItem[];
    nextPaginationKey?: string | null;
  };
};

type GetRemittanceNotesVariables = {
  input: GetRemittanceNotesInput;
};

type AddRemittanceNoteResponse = {
  addRemittanceNote: RemittanceNoteItem;
};

type AddRemittanceNoteVariables = {
  input: AddRemittanceNoteInput;
};

type UseRemittanceNotesInput = {
  offeringID?: string | null;
  buyerCompanyID?: string | null;
  accountID?: string | null;
  limit?: number;
  resetKey?: string | number;
  enabled?: boolean;
};

const normalizeCreatedAt = (value: number | string): string => {
  if (typeof value === 'string') {
    return value;
  }
  return new Date(value).toISOString();
};

const mapToNote = (note: RemittanceNoteItem): Note => ({
  noteID: note.noteID,
  dealID: note.dealID,
  loanID: note.loanID || undefined,
  fileName: note.fileName,
  note: note.note,
  authorID: note.authorID,
  authorRole: note.authorRole,
  buyerCompanyID: note.buyerCompanyID,
  sellerCompanyID: note.sellerCompanyID,
  createdAt: normalizeCreatedAt(note.createdAt),
});

const mergeNotes = (existing: Note[], incoming: Note[]) => {
  const map = new Map(existing.map((note) => [note.noteID, note]));
  incoming.forEach((note) => {
    map.set(note.noteID, note);
  });
  return Array.from(map.values());
};

export const useRemittanceNotes = ({
  offeringID,
  buyerCompanyID,
  accountID,
  limit = DEFAULT_LIMIT,
  resetKey,
  enabled = true,
}: UseRemittanceNotesInput) => {
  const { data: userData } = useQuery<GetUserCompany>(GET_USER_COMPANY);
  const resolvedBuyerCompanyID = buyerCompanyID || userData?.user.company.id;
  const scopeKey = `${offeringID || ''}-${resolvedBuyerCompanyID || ''}-${accountID || ''}-${resetKey ?? ''}`;
  const scopeKeyRef = useRef(scopeKey);
  const [notes, setNotes] = useState<Note[]>([]);
  const [nextPaginationKey, setNextPaginationKey] = useState<string | null | undefined>(null);
  const [isAutoLoadingMore, setIsAutoLoadingMore] = useState(false);
  const lastPaginationKeyRef = useRef<string | null | undefined>(null);
  const shouldFetch = enabled && Boolean(resolvedBuyerCompanyID) && Boolean(accountID);

  const { data, loading, fetchMore, networkStatus } = useQuery<
    GetRemittanceNotesResponse,
    GetRemittanceNotesVariables
  >(GET_REMITTANCE_NOTES, {
    variables: {
      input: {
        offeringID: offeringID || '',
        buyerCompanyID: resolvedBuyerCompanyID || '',
        accountID: accountID || '',
        limit,
      },
    },
    skip: !shouldFetch,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (scopeKeyRef.current !== scopeKey) {
      scopeKeyRef.current = scopeKey;
      setNotes([]);
      setNextPaginationKey(null);
      setIsAutoLoadingMore(false);
      lastPaginationKeyRef.current = null;
    }
  }, [scopeKey]);

  useEffect(() => {
    if (!data?.getRemittanceNotes) return;
    const mappedNotes = data.getRemittanceNotes.notes.map((note) => mapToNote(note));
    setNotes((prev) => mergeNotes(prev, mappedNotes));
    setNextPaginationKey(data.getRemittanceNotes.nextPaginationKey);
  }, [data]);

  useEffect(() => {
    if (!nextPaginationKey) {
      setIsAutoLoadingMore(false);
      lastPaginationKeyRef.current = null;
      return;
    }
    if (isAutoLoadingMore) return;
    if (lastPaginationKeyRef.current === nextPaginationKey) return;

    const loadRemaining = async () => {
      setIsAutoLoadingMore(true);
      lastPaginationKeyRef.current = nextPaginationKey;
      await fetchMore({
        variables: {
          input: {
            offeringID: offeringID || '',
            buyerCompanyID: resolvedBuyerCompanyID || '',
            accountID: accountID || '',
            limit,
            paginationKey: nextPaginationKey,
          },
        },
      });
      setIsAutoLoadingMore(false);
    };

    loadRemaining();
  }, [
    fetchMore,
    isAutoLoadingMore,
    limit,
    nextPaginationKey,
    accountID,
    offeringID,
    resolvedBuyerCompanyID,
  ]);

  return {
    notes,
    loading,
    hasMore: Boolean(nextPaginationKey),
    isLoadingMore: isAutoLoadingMore || networkStatus === NetworkStatus.fetchMore,
  };
};

type UseAddRemittanceNoteInput = {
  offeringID?: string | null;
  buyerCompanyID?: string | null;
  accountID?: string | null;
};

export const useAddRemittanceNote = ({
  offeringID,
  buyerCompanyID,
  accountID,
}: UseAddRemittanceNoteInput) => {
  const client = useApolloClient();
  const { data: userData } = useQuery<GetUserCompany>(GET_USER_COMPANY);
  const resolvedBuyerCompanyID = buyerCompanyID || userData?.user.company.id;
  const [addNoteMutation, { loading: addingNote }] = useMutation<
    AddRemittanceNoteResponse,
    AddRemittanceNoteVariables
  >(ADD_REMITTANCE_NOTE);

  const addNote = useCallback(
    async (text: string) => {
      if (!accountID) return;
      await addNoteMutation({
        variables: {
          input: {
            offeringID: offeringID || '',
            buyerCompanyID: resolvedBuyerCompanyID || '',
            accountID,
            note: text,
          },
        },
      });
      await client.query<GetRemittanceNotesResponse, GetRemittanceNotesVariables>({
        query: GET_REMITTANCE_NOTES,
        fetchPolicy: 'network-only',
        variables: {
          input: {
            offeringID: offeringID || '',
            buyerCompanyID: resolvedBuyerCompanyID || '',
            accountID,
            limit: DEFAULT_LIMIT,
          },
        },
      });
    },
    [accountID, addNoteMutation, client, offeringID, resolvedBuyerCompanyID],
  );

  return {
    addNote,
    addingNote,
  };
};
